/**
 * What a lift's state actually MEANS to the person looking at it.
 *
 * Before this file, the Lifts tab drew one red "Fault" chip off a
 * single boolean, and that chip stood for five unrelated conditions: a
 * bridge nobody had armed, an inventory that failed validation, a dead
 * lift controller, a car taken by fire service, and a car in
 * emergency. Four of those have completely different answers and one
 * of them is not a fault at all. At Hotel NoMad all six lifts read
 * "Fault" for the first condition — the cloud published MODE_OFFLINE
 * because it believed the bridge was disarmed — and no console
 * anywhere could say so.
 *
 * The verdict is derived HERE and nowhere else. Every lift surface
 * (the table column, the panel above it, the detail drawer) calls into
 * this, so there is exactly one reading of arm state in the SPA. A
 * second reading of one field is the class of bug this whole change
 * exists to end, and a screen that re-derived "armed" from its own
 * inline ternary would be that bug at a smaller scale.
 *
 * Every verdict carries a `meaning` AND an `action`, because "Fault"
 * failed operators in both directions: it never said what was wrong,
 * and it never said whether they could do anything about it.
 */
import type { LiftViewModel } from '../../api/adapters';
import type { LiftWriteGate } from '../../api/v1';
import type { StatusKind } from '../../design-system/tokens';

export type LiftDispatchStatusKind =
  /** Validated and armed: a robot may call a lift on this site. */
  | 'armed'
  /** Tier 1 passed, tier 2 has not. Dispatch refused, RELEASE still works. */
  | 'disarmed'
  /** Tier 1 failed. EVERY write refused, release included. */
  | 'unvalidated'
  /** The lift itself reports offline while the platform is armed and validated. */
  | 'lift_offline'
  /** The lift reports a mode RMF can only read, never set. */
  | 'emergency'
  /** Fire service has the car; the fire panel owns it, not us. */
  | 'fire_service'
  /** The gate has not been read yet, or could not be. NOT the same as disarmed. */
  | 'unknown'
  /**
   * Nothing is publishing state for this car at all: it is in the
   * site's `lifts_registry` and absent from the live snapshot. Kept
   * apart from every verdict above because all of those are readings
   * OF a lift, and this one says there is no reading — and because it
   * outranks them: an armed, validated gate says nothing about a car
   * whose adapter is not talking, and rendering such a row as "Armed"
   * is precisely the confident-wrong answer this file was written to
   * stop giving.
   */
  | 'no_state'
  /**
   * The api-server answered 404: this deployment serves no write gate
   * at all. Kept apart from 'unknown' because they call for different
   * things from the person reading them — 'unknown' is a fault to
   * chase, this is a version to deploy — and because conflating them
   * would leave an operator refreshing a page that is never going to
   * answer.
   */
  | 'unsupported';

export interface LiftDispatchStatus {
  readonly kind: LiftDispatchStatusKind;
  /** Chip colour. Deliberately not all 'error': fire service is a
   *  building in a state it is supposed to be able to reach, and a
   *  disarmed bridge is the system working as designed. */
  readonly chip: StatusKind;
  /** Two words at most — this sits in a table cell. */
  readonly label: string;
  /** What is wrong, in one sentence. */
  readonly meaning: string;
  /** What the person reading it can do about it, in one sentence —
   *  including "nothing, and here is who owns it". */
  readonly action: string;
  /** The bridge's own words, passed through when it has any. Empty
   *  string when the verdict does not come from the bridge. */
  readonly detail: string;
}

const UNKNOWN: LiftDispatchStatus = {
  kind: 'unknown',
  chip: 'offline',
  label: 'Unknown',
  // Said carefully. The old failure was a console asserting "not
  // armed" when it had simply never been told, so an unread gate must
  // never render as a disarmed one.
  meaning: 'The console has not been able to read this site’s lift write gate, so it cannot say whether dispatching writes are armed.',
  action: 'Check that the api-server is reachable. The bridge may well be armed and working — this says nothing either way.',
  detail: '',
};

/**
 * The api-server has no write-gate read path. Distinct from UNKNOWN,
 * and every word of it is chosen not to imply a gate state: a console
 * that cannot see the flag knows nothing about the bridge, which may
 * be armed, disarmed or refusing everything on a failed inventory.
 */
const UNSUPPORTED: LiftDispatchStatus = {
  kind: 'unsupported',
  chip: 'offline',
  label: 'Not available',
  meaning:
    'This deployment’s api-server does not publish the lift write gate, so arm state cannot be read from this console at all.',
  action:
    'Read and change arm state on the edge box’s own dashboard until an api-server that serves the gate is deployed here. This says nothing about whether the bridge is armed.',
  detail: '',
};

/**
 * A lift the building map knows about and the live snapshot does not.
 *
 * The doors half of the same outage is what motivated it: the MEGAZO
 * adapter at sg-nomad-01 could not log in, published nothing, and the
 * console showed no door rather than a broken one. A lift bridge that
 * dies takes its cars off the list the same way, so the lift list
 * answers it the same way — the row stays, and says why it is empty.
 */
const NO_STATE: LiftDispatchStatus = {
  kind: 'no_state',
  chip: 'offline',
  label: 'No data',
  meaning:
    'This lift is in the site’s registry — the building map knows it exists — but nothing is publishing state for it, so the console has no reading of the car at all.',
  action:
    'Check the lift bridge on the edge box and its link to the controller. Until it publishes, no command can be sent to this car from here, and everything above is an absence rather than a reading.',
  detail: '',
};

/**
 * The building's fire service has the car. A reading of the car, not of
 * the gate, so it carries no gate detail and claims nothing about arm
 * state either way.
 */
const FIRE_SERVICE: LiftDispatchStatus = {
  kind: 'fire_service',
  chip: 'warning',
  label: 'Fire service',
  meaning:
    'The car has been taken by fire service and answers the fire panel only. Nothing the platform sends will move it, armed or not.',
  action: 'Wait for the fire panel to release it. Nothing to do here, and nothing is broken.',
  detail: '',
};

function unvalidated(gate: LiftWriteGate): LiftDispatchStatus {
  return {
    kind: 'unvalidated',
    chip: 'error',
    label: 'Not validated',
    meaning:
      'The lift bridge could not match its configured addresses against the elevator controller, so it is refusing every write on this site — dispatch and release alike.',
    action:
      'Fix the item list on the edge box until validation passes. Arming is blocked until it does, and cannot be forced from here.',
    detail: gate.detail,
  };
}

function disarmed(gate: LiftWriteGate): LiftDispatchStatus {
  return {
    kind: 'disarmed',
    chip: 'warning',
    label: 'Not armed',
    meaning:
      'Validation passed, but nobody has armed dispatching writes on this site’s lift bridge, so robots cannot call a lift. Arm state is never restored by a restart — a bridge that rebooted overnight comes back disarmed.',
    action:
      'Arm dispatching writes, which takes a supervisor. A release still works while disarmed, so a stuck car can always be freed.',
    detail: gate.detail,
  };
}

function armed(gate: LiftWriteGate): LiftDispatchStatus {
  return {
    kind: 'armed',
    chip: 'active',
    label: 'Armed',
    meaning: 'Validated and armed: robots may call the lifts on this site.',
    action:
      'Disarm to stand the lift bank down. A release still works afterwards, so disarming never traps a car.',
    detail: gate.detail,
  };
}

/**
 * The SITE-wide half of the verdict, with no lift in hand. This is
 * what the panel above the Lifts table and the arm control itself
 * render, and it is the same function the per-lift verdict starts
 * from — the two can never drift apart because there is only one.
 *
 * Note the ordering: a failed inventory outranks the arm flag. An
 * armed bridge whose LATER inventory failed is a real, publishable
 * state (nothing disarms a bridge when validation lapses), and in it
 * tier 1 is refusing every write regardless — invariant I5. Reading
 * that state as "Armed" would be the most dangerous thing this screen
 * could say.
 */
export function writeGateStatus(gate: LiftWriteGate | null | undefined): LiftDispatchStatus {
  // `null` and `undefined` are two different answers and must stay
  // that way. `null` is `fetchLiftWriteGate` reporting a 404 -- there
  // is no gate on this api-server; `undefined` is the query not
  // having one yet, whether because it is still in flight or because
  // the read failed. Neither is a gate reading, and neither may be
  // rendered as one.
  if (gate === null) return UNSUPPORTED;
  if (!gate) return UNKNOWN;
  if (!gate.writes_permitted) return unvalidated(gate);
  if (!gate.writes_armed) return disarmed(gate);
  return armed(gate);
}

/**
 * The per-lift verdict: the site-wide gate, then whatever this
 * particular car reports about itself -- except fire service, which
 * outranks the gate (see below).
 *
 * The interesting case, and the reason this function exists, is
 * MODE_OFFLINE. A disarmed bridge and a dead controller produce the
 * identical `mode: 'off'` with the identical `fault: true` — the lift
 * row cannot tell them apart on its own, which is exactly why the
 * Hotel NoMad outage looked like six broken elevators. Cross-checking
 * against the site gate separates them: offline while disarmed is
 * explained by the disarm, offline while armed is the lift.
 */
export function liftDispatchStatus(
  lift: Pick<LiftViewModel, 'mode' | 'fault' | 'hasLiveState'>,
  gate: LiftWriteGate | null | undefined,
): LiftDispatchStatus {
  // FIRST, ahead of even the gate checks. Everything below reads a
  // field of a lift that reported something; a registry-only row has
  // reported nothing, so `mode`/`fault` there are absences and reading
  // them would produce a verdict about a car nobody has heard from.
  // With an armed, validated gate the fall-through at the bottom would
  // have called such a row "Armed", which is the single most
  // misleading thing this column could say during commissioning.
  if (!lift.hasLiveState) return NO_STATE;

  // FIRE NEXT, ahead of every gate check (twin slice 1, W6 step 3).
  // Fire service is a fact about the CAR, read off its own mode, and no
  // state of the write gate changes it: an unreadable gate says nothing
  // about whether the building has the lift, and a disarmed or
  // unvalidated one is beside the point while the fire panel owns it.
  // The connector orders it the same way -- `vocab.py::mode_class`
  // checks the fire modes before any gate, because "offline because
  // writes are disarmed" is the wrong thing to say about a car under
  // fire recall. Before this, a fire-service car read "Unknown" or "Not
  // validated" whenever the gate was not armed, which is the one time
  // an operator most needs to know the building has taken it.
  if (lift.mode === 'fire') return FIRE_SERVICE;

  if (gate === null) return UNSUPPORTED;
  if (!gate) return UNKNOWN;
  // Tier 1 first: nothing the lift says about itself can be acted on
  // while every write is being refused anyway.
  if (!gate.writes_permitted) return unvalidated(gate);

  // MODE_EMERGENCY(5) has no v1 equivalent and collapses to 'unknown'
  // in the worker's mapping, so the fault flag is the only thing that
  // separates it from MODE_UNKNOWN(0) — a lift that simply has not
  // reported a mode yet.
  if (lift.fault && lift.mode === 'unknown') {
    return {
      kind: 'emergency',
      chip: 'error',
      label: 'Emergency',
      meaning:
        // No "RMF" where a hotel reader sees it (Akash, 2026-10-31).
        'The car reports an emergency mode, which the system can read but never set. It has no way into or out of this state.',
      action: 'This is the lift, not the platform. Call whoever maintains the lift.',
      detail: '',
    };
  }

  // Ahead of the offline check on purpose: while the site is disarmed,
  // MODE_OFFLINE is the EXPECTED reading — it is what the cloud
  // adapter publishes for a bridge it believes is not armed — so
  // calling it a lift problem would send an engineer to the machine
  // room over a switch nobody had flipped.
  if (!gate.writes_armed) return disarmed(gate);

  if (lift.mode === 'off') {
    return {
      kind: 'lift_offline',
      chip: 'error',
      label: 'Lift offline',
      meaning:
        'Writes are armed and validated for this site, but this car reports itself offline — so this one is the lift or its controller link, not the arm state.',
      action:
        'Check the lift bridge on the edge box and the controller behind it. The other lifts on this site are unaffected.',
      detail: '',
    };
  }

  return armed(gate);
}

// ----------------------------------------------------------------------------
// What the verdict means for the CONTROLS drawn next to it.
//
// Both functions below take a `LiftDispatchStatus` rather than a
// `LiftWriteGate`, and that is the whole reason they exist here
// instead of as ternaries in the panel's JSX. The panel used to decide
// separately: the chip came from `writeGateStatus()`, the Arm button's
// disabled state came from an inline `gate?.writes_permitted === true`,
// and its tooltip came from a second inline ternary that said "Blocked
// by validation" for every disabled state there was. So with no read
// path at all -- which was every deployment -- the chip honestly read
// "the console could not read the gate" while the tooltip an inch
// below it asserted a failed inventory the console had never seen.
// One of them was inventing a cause. Derived from the one verdict,
// they cannot.
// ----------------------------------------------------------------------------

/**
 * Why the Arm control is refused, in the words to put on it — or
 * `undefined` when it is not refused, which is the ONLY thing that may
 * enable the button.
 *
 * Note what is not here: no fallback that treats an unreadable gate as
 * permission. A console that cannot see tier 1 must not offer tier 2,
 * because the one thing worse than an operator who cannot arm a lift
 * bank is an operator who thinks they have.
 */
export function armBlockedReason(status: LiftDispatchStatus): string | undefined {
  switch (status.kind) {
    case 'disarmed':
      // Validated, and nobody has armed it: the one state in which
      // arming is a thing a person can do from here.
      return undefined;
    case 'unvalidated':
      return 'Blocked by validation: the bridge refuses every write until its item list matches the controller.';
    case 'unknown':
      return 'The console cannot read this site’s write gate, so it will not offer to arm it. This does not mean the bridge is disarmed — it means nothing here knows either way.';
    case 'unsupported':
      return 'This deployment does not publish the lift write gate, so arm state cannot be read or changed from this console.';
    case 'armed':
    case 'lift_offline':
    case 'emergency':
      // All three are gate-armed-and-validated readings; whatever else
      // is wrong, tier 2 is already on and arming again is not the
      // answer to any of them.
      return 'Dispatching writes are already armed for this site.';
    case 'fire_service':
      // NOT with the three above any more: fire outranks the gate, so
      // this verdict no longer implies an armed one, and saying
      // "already armed" here could be false. Unreachable from the Arm
      // control (driven by the site-wide `writeGateStatus()`, which
      // never reads a car), but spelled out for the reason `no_state`
      // is below.
      return 'Fire service has this car, and arming would not change that. Arm state is a site-wide setting; read it from the panel above the table.';
    case 'no_state':
      // Unreachable in practice -- the Arm control is driven by
      // `writeGateStatus()`, which is site-wide and has no lift in hand
      // -- but spelled out rather than defaulted, because the one thing
      // this switch must never do is hand back `undefined` (which
      // ENABLES the button) for a state nobody considered.
      return 'Nothing is publishing state for this lift, so this console cannot say anything about it. Arm state is a site-wide setting; read it from the panel above the table.';
  }
}

/**
 * Whether the console is holding an actual reading of the gate, as
 * opposed to a verdict whose content is "I cannot tell".
 *
 * Used for the sentence shown after an arm request is accepted. The
 * bridge's own refusal — "confirmation required", "inventory not
 * validated: …" — travels on its MQTT ack topic and reaches no
 * browser, so the ONLY evidence a console can offer that an arm
 * actually took is the gate changing underneath it. Where there is no
 * gate reading there is no evidence either, and promising the operator
 * that "the state above will update" would be a promise nothing can
 * keep.
 */
export function gateIsReadable(status: LiftDispatchStatus): boolean {
  return status.kind !== 'unknown' && status.kind !== 'unsupported';
}
